import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, throwError } from 'rxjs';
import {
  FinanceExecutionReport,
  FinanceExceptionResetResult,
  FinanceExecutionOutcomePage,
  AmazonElementMapping,
  ElementMappingView,
  FinanceConfiguration,
  FinanceConfigurationView,
  FinanceDashboard,
  FinanceJobOverview,
  FinanceJobRunResult,
  FinanceJobStage,
  MappingSuggestion,
  MappingCompletionResult,
  TransactionCommand,
  TransactionCommandResult,
  TransactionDetail,
  TransactionExplanation,
  TransactionFacets,
  TransactionFilter,
  TransactionPage,
  AmazonFinanceValidationResult
} from './amazon-finance.models';

@Injectable({ providedIn: 'root' })
export class AmazonFinanceService {
  private readonly baseUrl = 'json/amazon/v2/finance';

  constructor(private readonly http: HttpClient) {}

  getDashboard(): Observable<FinanceDashboard> {
    return this.http.get<FinanceDashboard>(`${this.baseUrl}/dashboard`);
  }

  getJobs(): Observable<FinanceJobOverview> {
    return this.http.get<FinanceJobOverview>(`${this.baseUrl}/jobs`, {
      // Job status is polled while executions run and must bypass Gung's GET response cache.
      headers: { maxAge: '-1' }
    });
  }

  getExecutionReports(page = 0): Observable<FinanceExecutionReport[]> {
    return this.http.get<FinanceExecutionReport[]>(`${this.baseUrl}/execution-reports`, {
      params: { page, size: 25 },
      headers: { maxAge: '-1' }
    });
  }

  getExecutionReport(id: string): Observable<FinanceExecutionReport> {
    return this.http.get<FinanceExecutionReport>(`${this.baseUrl}/execution-reports/${encodeURIComponent(id)}`, {
      headers: { maxAge: '-1' }
    });
  }

  getExecutionOutcomes(id: string, page = 0, status = ''): Observable<FinanceExecutionOutcomePage> {
    return this.http.get<FinanceExecutionOutcomePage>(
      `${this.baseUrl}/execution-reports/${encodeURIComponent(id)}/outcomes`,
      {
        params: { page, size: 50, status },
        headers: { maxAge: '-1' }
      }
    );
  }

  previewExceptionReset(id: string): Observable<FinanceExceptionResetResult> {
    return this.http.get<FinanceExceptionResetResult>(`${this.baseUrl}/execution-reports/${encodeURIComponent(id)}/exception-reset`, {
      headers: { maxAge: '-1' }
    });
  }

  resetReportExceptions(id: string): Observable<FinanceExceptionResetResult> {
    return this.http.post<FinanceExceptionResetResult>(`${this.baseUrl}/execution-reports/${encodeURIComponent(id)}/exception-reset`, null);
  }

  previewMappingReset(id: string, status: string): Observable<FinanceExceptionResetResult> {
    return this.http.get<FinanceExceptionResetResult>(`${this.baseUrl}/execution-reports/${encodeURIComponent(id)}/mapping-reset`, {
      params: { status }, headers: { maxAge: '-1' }
    });
  }

  resetReportMappings(id: string, status: string): Observable<FinanceExceptionResetResult> {
    return this.http.post<FinanceExceptionResetResult>(`${this.baseUrl}/execution-reports/${encodeURIComponent(id)}/mapping-reset`, null, {
      params: { status }
    });
  }

  previewMissingMappingsBacklog(): Observable<FinanceExceptionResetResult> {
    return this.http.get<FinanceExceptionResetResult>(`${this.baseUrl}/execution-reports/missing-mappings-reset`, {
      headers: { maxAge: '-1' }
    });
  }

  resetMissingMappingsBacklog(): Observable<FinanceExceptionResetResult> {
    return this.http.post<FinanceExceptionResetResult>(`${this.baseUrl}/execution-reports/missing-mappings-reset`, null);
  }

  runJob(stage: FinanceJobStage): Observable<FinanceJobRunResult> {
    return this.http.post<FinanceJobRunResult>(`${this.baseUrl}/jobs/${stage}/run`, null);
  }

  getTransactions(filter: TransactionFilter): Observable<TransactionPage> {
    let params = new HttpParams()
      .set('page', filter.page)
      .set('size', filter.size)
      .set('sort', filter.sort)
      .set('direction', filter.direction)
      .set('missingMappingsOnly', filter.missingMappingsOnly);

    const optional: Record<string, string> = {
      q: filter.q,
      postedFrom: filter.postedFrom,
      postedTo: filter.postedTo,
      transactionType: filter.transactionType,
      customerRevenueStatus: filter.customerRevenueStatus,
      amazonFeeCostStatus: filter.amazonFeeCostStatus
    };
    Object.entries(optional).forEach(([key, value]) => {
      if (value) {
        params = params.set(key, value);
      }
    });
    const multiValueFilters: Array<[string, string[]]> = [
      ['marketplace', filter.marketplace],
      ['currency', filter.currency],
      ['mappingStatus', filter.mappingStatus]
    ];
    multiValueFilters.forEach(([key, values]) => {
      values.forEach(value => (params = params.append(key, value)));
    });
    return this.http.get<TransactionPage>(`${this.baseUrl}/transactions`, {
      params,
      headers: { maxAge: '-1' }
    });
  }

  getTransactionFacets(): Observable<TransactionFacets> {
    return this.http.get<TransactionFacets>(`${this.baseUrl}/transactions/facets`);
  }

  getTransaction(id: string): Observable<TransactionDetail> {
    return this.http.get<TransactionDetail>(`${this.baseUrl}/transactions/${encodeURIComponent(id)}`, {
      headers: { maxAge: '-1' }
    });
  }

  explainTransaction(id: string, language: string): Observable<TransactionExplanation> {
    return this.http.post<TransactionExplanation>(
      `${this.baseUrl}/transactions/${encodeURIComponent(id)}/explanation`,
      { language }
    );
  }

  command(command: TransactionCommand, transactionIds: string[]): Observable<TransactionCommandResult> {
    return this.http.post<TransactionCommandResult>(`${this.baseUrl}/transactions/commands/${command}`, {
      transactionIds
    });
  }

  getMappings(): Observable<ElementMappingView[]> {
    return this.http.get<ElementMappingView[]>(`${this.baseUrl}/mappings`);
  }

  validateMapping(mapping: AmazonElementMapping): Observable<AmazonFinanceValidationResult> {
    return this.http.post<AmazonFinanceValidationResult>(`${this.baseUrl}/mappings/validate`, mapping);
  }

  saveMapping(mapping: AmazonElementMapping): Observable<ElementMappingView> {
    return this.http.put<ElementMappingView>(`${this.baseUrl}/mappings/${encodeURIComponent(mapping.id)}`, mapping);
  }

  deleteOrphanMapping(mappingId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/mappings/${encodeURIComponent(mappingId)}`);
  }

  completeMapping(mapping: AmazonElementMapping): Observable<MappingCompletionResult> {
    return this.http
      .put<MappingCompletionResult>(`${this.baseUrl}/mappings/${encodeURIComponent(mapping.id)}/complete-and-queue`, mapping)
      .pipe(
        catchError(error => {
          if (error?.status !== 404 && error?.status !== 405) {
            return throwError(() => error);
          }
          return this.saveMapping(mapping).pipe(
            map(saved => ({
              mapping: saved,
              affectedTransactions: saved.blockedTransactions,
              remapResult: {
                command: 'REMAP',
                requested: 0,
                updated: [],
                rejected: [],
                mappingStatusCounts: {}
              },
              remainingBlockedTransactions: saved.blockedTransactions,
              truncated: false,
              automaticRemapSupported: false
            }))
          );
        })
      );
  }

  suggestMapping(mappingId: string, language: string): Observable<MappingSuggestion> {
    return this.http.post<MappingSuggestion>(`${this.baseUrl}/mappings/${encodeURIComponent(mappingId)}/suggestion`, {
      language
    });
  }

  rejectMappingSuggestion(mappingId: string, suggestionId: string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/mappings/${encodeURIComponent(mappingId)}/suggestion/${encodeURIComponent(suggestionId)}/feedback`,
      { outcome: 'REJECTED' }
    );
  }

  getConfiguration(): Observable<FinanceConfigurationView> {
    return this.http.get<FinanceConfigurationView>(`${this.baseUrl}/configuration`);
  }

  validateConfiguration(configuration: FinanceConfiguration): Observable<AmazonFinanceValidationResult> {
    return this.http.post<AmazonFinanceValidationResult>(`${this.baseUrl}/configuration/validate`, configuration);
  }

  saveConfiguration(configuration: FinanceConfiguration): Observable<FinanceConfigurationView> {
    return this.http.put<FinanceConfigurationView>(`${this.baseUrl}/configuration`, configuration);
  }
}
